import type { BuyerSequenceRenderedDto } from "@/api/buyerSequence";
import { cn } from "@/design-system/lib/utils";

/**
 * One rendered message block, shown as the reader will send it — with the
 * merged values highlighted ON SCREEN ONLY (L4).
 *
 * <p>The highlight is a property of this preview, never of what is copied: COPY
 * takes the block's `plain`, which the server assembles, so no span, class or
 * marker here can reach a clipboard. A run the template wrote itself renders as
 * plain text; a run a merge field produced gets a quiet gold tint; a REQUIRED
 * field with no value gets the warning treatment, because its placeholder must
 * never be copied and the card's Copy is disabled while one is present.</p>
 *
 * <p>🔴 NOT EDITABLE, deliberately — no textarea, no contentEditable. The words
 * are changed in Edit wording, where a change is saved and versioned; an edit
 * made here would be sent and then lost, and the log would record text nobody
 * can reproduce. `whitespace-pre-wrap` keeps the line breaks the recipient
 * will see.</p>
 */
export function RenderedBlock({
  block,
  className,
  "data-testid": testId,
}: {
  block: BuyerSequenceRenderedDto;
  className?: string;
  "data-testid"?: string;
}) {
  return (
    <div
      className={cn("whitespace-pre-wrap break-words text-small text-onyx", className)}
      data-testid={testId}
    >
      {block.segments.map((seg, i) => {
        if (seg.missing) {
          return (
            <span
              key={i}
              className="rounded-sm bg-status-warning/15 text-status-warning-ink"
              data-outreach-field={seg.field ?? undefined}
              data-missing="true"
            >
              {seg.text}
            </span>
          );
        }
        if (seg.field) {
          return (
            <span
              key={i}
              className="rounded-sm bg-gold-tint"
              data-outreach-field={seg.field}
            >
              {seg.text}
            </span>
          );
        }
        return <span key={i}>{seg.text}</span>;
      })}
    </div>
  );
}
